import { Static, Union } from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

import { AnthropicModelLiteral } from "./anthropicTypes.js";
import { KimiModelLiteral } from "./chatCompletionsTypes.js";
import {
  EffortLevel,
  ModelSpeed,
  getEffortDisplayLabel,
} from "./effortLevel.js";
import { OpenAiChatModelLiteral } from "./openAiTypes.js";

export const ModelNameLiteral = Union(
  ...AnthropicModelLiteral.alternatives,
  ...OpenAiChatModelLiteral.alternatives,
  ...KimiModelLiteral.alternatives,
);
export type ModelName = Static<typeof ModelNameLiteral>;
export const ModelName = getNormalEnum(ModelNameLiteral);

/**
 * Human-readable labels for the models we surface to users
 */
export const MODEL_NAME_LABELS = {
  [ModelName.CLAUDE_4_7_OPUS]: "Opus 4.7",
  [ModelName.CLAUDE_4_8_OPUS]: "Opus 4.8",
  [ModelName.CLAUDE_4_6_SONNET]: "Sonnet 4.6",
  [ModelName.CLAUDE_5_SONNET]: "Sonnet 5",
  [ModelName.CLAUDE_FABLE_5]: "Fable 5",
  [ModelName.GPT_55]: "GPT 5.5",
  [ModelName.GPT_56]: "GPT 5.6 Sol",
  [ModelName.KIMI_K_2_7_THINKING]: "Kimi 2.7",
} satisfies Partial<Record<ModelName, string>>;

/**
 * Resolve a (possibly persisted/unknown) model string to a human-readable label.
 */
export function getModelDisplayLabel(
  model: string | null | undefined,
): string | null {
  if (model == null) {
    return null;
  }
  if (!ModelNameLiteral.guard(model)) {
    return model;
  }
  const labels: Partial<Record<ModelName, string>> = MODEL_NAME_LABELS;
  return labels[model] ?? model;
}

/**
 * Resolve a model and optional effort to the display label shown in user-facing
 * eval surfaces.
 */
export function formatModelEffortLabel(
  model: string | null | undefined,
  effortLevel: string | null | undefined,
): string | null {
  const modelLabel = getModelDisplayLabel(model);
  if (modelLabel == null) {
    return null;
  }

  const effortLabel = getEffortDisplayLabel(effortLevel);
  return effortLabel == null ? modelLabel : `${modelLabel} (${effortLabel})`;
}

/**
 * Coerce a persisted thread model value for GraphQL and runtime resolution.
 * Returns null for values that are no longer recognized ModelName enum members.
 */
export function resolvePersistedModelName(
  model: string | null | undefined,
): ModelName | null {
  if (model == null) {
    return null;
  }
  return ModelNameLiteral.guard(model) ? model : null;
}

/**
 * Find the matching, currently-usable picker option for a model name. Returns
 * `undefined` when `model` is null/"auto" or when the model is absent or
 * disabled in `options`.
 */
export function findAvailableModelOption<
  T extends { model: ModelName | null; disabled: boolean },
>(options: readonly T[], model: ModelName | null): T | undefined {
  if (model == null) {
    return undefined;
  }
  return options.find((opt) => opt.model === model && !opt.disabled);
}

export interface ModelSelection {
  model: ModelName | null;
  effortLevel: EffortLevel | null;
  speed: ModelSpeed | null;
}

export const AUTO_MODEL_SELECTION: ModelSelection = {
  model: null,
  effortLevel: null,
  speed: null,
};

/**
 * Resolve a stored/selected (model, effort, speed) against the org's currently
 * available picker options, mirroring the server's `resolveUserSelection`:
 * returns "auto" (all-null) unless the model is available, the effort is
 * supported, and — for fast — a fast variant exists for that effort (otherwise
 * the speed drops to standard). Shared by the org default-model setting row, the
 * prompt-bar seeding hook, and anywhere else a stored selection is validated on
 * read so the answer never diverges.
 */
export function resolveAvailableModelSelection<
  T extends {
    model: ModelName | null;
    disabled: boolean;
    supportedEfforts: readonly EffortLevel[];
    supportedFastEfforts?: readonly EffortLevel[] | null;
  },
>(selection: ModelSelection, options: readonly T[]): ModelSelection {
  const available = findAvailableModelOption(options, selection.model);
  if (
    available == null ||
    selection.effortLevel == null ||
    !available.supportedEfforts.includes(selection.effortLevel)
  ) {
    return AUTO_MODEL_SELECTION;
  }
  const shouldUseFast =
    selection.speed === "fast" &&
    (available.supportedFastEfforts ?? []).includes(selection.effortLevel);
  return {
    model: selection.model,
    effortLevel: selection.effortLevel,
    speed: shouldUseFast ? "fast" : null,
  };
}
